import {
	Dashboard,
	DashboardPanel,
	DashboardScope,
	DashboardScopeType,
	DashboardSearchOptions,
	useApi,
} from "@soft-db/monitoring-api-client";
import { useCurrentUser } from "../../api/current-user";
import { Plugins } from "../../components/dashboard/plugins";
import { createRestrictiveInjectionState } from "../../composable/restrictive-injection-state";
import { useIsPublic } from "../../plugins";
import { usePermissionStore } from "../permission";
import { useScopeStore } from "../scope";
import { useDashboardState } from "./dashboard-state";

export type DashboardLayoutType = DashboardScopeType | "default";

export const [provideDashboardLayout, useDashboardLayout] = createRestrictiveInjectionState(() => {
	const { context } = useDashboardState();

	const api = useApi().dashboard();

	const { currentUser } = useCurrentUser();
	const scopeStore = useScopeStore();
	const permissionStore = usePermissionStore();

	const selectedLayout = ref<DashboardLayoutType | null>(null);
	const availableLayouts = shallowReactive<Map<DashboardLayoutType, Dashboard>>(new Map());

	const isLoading = ref(true);

	function getLayoutTypeFromScope(scope: DashboardScope | null): DashboardLayoutType {
		return scope == null ? "default" : scope.type;
	}

	function getLayout(type: DashboardLayoutType) {
		return availableLayouts.get(type) || null;
	}

	function hasLayoutType(type: DashboardLayoutType) {
		return getLayout(type) !== null;
	}

	const isDistConfiguringSpace = computed(
		() => currentUser.value?.scope.type === "distributor" && scopeStore.scope.type === "space"
	);

	const isConfiguringPublicPage = computed(() => context.value?.publicPage != null);

	const currentDefault = computed(() => {
		if (hasLayoutType("space")) {
			return "space";
		}
		return "default";
	});

	const possibleLayoutsTypes = computed<DashboardLayoutType[]>(() => {
		if (isConfiguringPublicPage.value) {
			return [currentDefault.value, "public_page"];
		}

		if (isDistConfiguringSpace.value) {
			return ["default", "space"];
		}

		return [currentDefault.value, "user"];
	});

	const editableLayoutsTypes = computed<DashboardLayoutType[]>(() => {
		if (isConfiguringPublicPage.value && canCreatePublic.value) {
			return ["public_page"];
		}

		if (isDistConfiguringSpace.value) {
			return ["space"];
		}

		const layouts: DashboardLayoutType[] = [];
		if (canCreateDefault.value) {
			layouts.push(currentDefault.value);
		}

		if (canCreateOwn.value) {
			layouts.push("user");
		}

		return layouts;
	});

	const { t } = useI18n();
	function label(layout: DashboardLayoutType) {
		switch (layout) {
			case "default":
				if (!isDistConfiguringSpace.value) {
					return t("dashboard.layoutDefault");
				}
				return t("dashboard.layoutDefaultDist");

			case "space":
				if (!isDistConfiguringSpace.value) {
					return t("dashboard.layoutDefault");
				}
				return t("dashboard.layoutDefaultSpace");

			case "user":
				return t("dashboard.layoutUser");

			case "public_page":
				return t("dashboard.layoutPublicPage");
		}
	}

	function creationLabel(layout: DashboardLayoutType) {
		switch (layout) {
			case "user":
				return t("dashboard.createLayoutUser");

			// Not yet possibles...
			default:
				return label(layout);
		}
	}

	const currentLayout = computed<Dashboard | null>(() => {
		return selectedLayout.value != null ? getLayout(selectedLayout.value) : null;
	});

	// Set or reset the selected dashboard
	watch(availableLayouts, () => {
		if (currentLayout.value != null) {
			return;
		}

		if (availableLayouts.size === 0) {
			selectedLayout.value = null;
		}

		const layout = availableLayouts.values().next().value;
		if (layout == null) {
			return;
		}

		selectedLayout.value = getLayoutTypeFromScope(layout.scope);
	});

	const canCreateOwn = computed(() => permissionStore.hasPermission("dashboard.own.create"));
	const canCreateDefault = computed(() => permissionStore.hasPermission("dashboard.create"));
	const canCreatePublic = computed(() => permissionStore.hasPermission("public-page.update"));

	function isLayoutEditable(layout: DashboardLayoutType) {
		switch (layout) {
			case "space":
			case "default":
				return canCreateDefault.value;
			case "user":
				return canCreateOwn.value;
			case "public_page":
				return canCreatePublic.value;
		}
	}

	async function createInitialDashboard() {
		if (canCreateDefault.value) {
			return createDefaultLayout();
		}

		if (canCreateOwn.value) {
			return createUserLayout();
		}
	}

	function setLayouts(layouts: Dashboard[]) {
		for (const layout of layouts) {
			// Keep the legacy layout
			if (
				(layout.panels == null || layout.panels.length === 0) &&
				layout.template != null &&
				layout.template.panels != undefined
			) {
				layout.panels = layout.template.panels;
			}

			layout.panels = migratePanels(layout.panels);

			const type = getLayoutTypeFromScope(layout.scope);
			availableLayouts.set(type, layout);

			if (selectedLayout.value == null) {
				selectedLayout.value = type;
			}
		}
	}

	function migratePanels(panels: DashboardPanel[]): DashboardPanel[] {
		return panels.map(migratePanel);
	}

	function migratePanel(panel: DashboardPanel): DashboardPanel {
		const pluginDef = Plugins[panel.plugin.type];

		// Plugin not found, nothing to do
		if (pluginDef == null) {
			return panel;
		}

		// Version was set to 0 or null/undefined by default in some place, and to 1 in other.
		// We assume that the starting version is 1 for every plugin.
		if (panel.plugin.version === 0 || panel.plugin.version == null) {
			panel.plugin.version = 1;
		}

		while (panel.plugin.version < pluginDef.currentVersion) {
			const migration = pluginDef.migrations[panel.plugin.version];
			if (migration != null) {
				const options = migration({
					data: panel.plugin.data,
					options: panel.plugin.options,
				});

				panel.plugin.data = options.data;
				panel.plugin.options = options.options;
			}

			panel.plugin.version++;
		}

		return panel;
	}

	async function loadLayouts(site: string) {
		let searchOptions: DashboardSearchOptions | undefined;

		// Force search on space, to prevent getting the user own dashboard.
		if (isDistConfiguringSpace.value) {
			searchOptions = { scopeType: "space", scopeId: scopeStore.scope.id };
		}

		if (context.value?.publicPage != null) {
			searchOptions = {
				scopeType: "public_page",
				scopeId: context.value.publicPage,
			};
		}

		isLoading.value = true;
		try {
			const layouts = await api.fetch(site, searchOptions);
			setLayouts(layouts);
			if (layouts.length === 0) {
				await createInitialDashboard();
			}
		} finally {
			isLoading.value = false;
		}
	}

	async function resetLayouts() {
		availableLayouts.clear();

		if (context.value == null || currentUser.value == null) {
			return;
		}

		await loadLayouts(context.value.site);
	}

	watchImmediate(context, async (context, prevContext) => {
		if (context?.site === prevContext?.site) {
			return;
		}

		await resetLayouts();
	});

	// Reset dashboard when we leave or enter a space
	watch(isDistConfiguringSpace, async (prev, current) => {
		if (prev === current) {
			return;
		}

		resetLayouts();
	});

	function changeLayout(type: DashboardLayoutType) {
		if (getLayout(type) != null) {
			selectedLayout.value = type;
		}
	}

	async function createLayout(scope: DashboardScope | null, fromCurrent?: boolean) {
		if (context.value == null) {
			return;
		}

		const type = getLayoutTypeFromScope(scope);

		if (hasLayoutType(type)) {
			return;
		}

		const createOptions: DashboardSearchOptions = {};
		if (scope != null) {
			createOptions.scopeType = scope.type;
			createOptions.scopeId = scope.id;
		}
		const from = fromCurrent ? currentLayout.value?.id || undefined : undefined;

		const createdDashboard = await api.create(context.value.site, createOptions, from);

		// setting the layouts will set the selectedLayout
		selectedLayout.value = null;
		setLayouts([createdDashboard]);
	}

	async function createUserLayout(fromCurrent?: boolean) {
		if (!canCreateOwn.value || currentUser.value == null) {
			return;
		}

		return createLayout({ type: "user", id: currentUser.value.id }, fromCurrent);
	}

	async function createPublicLayout(fromCurrent?: boolean) {
		if (!canCreatePublic.value || context.value?.publicPage == null) {
			return;
		}

		return createLayout({ type: "public_page", id: context.value.publicPage }, fromCurrent);
	}

	async function createDefaultLayout(fromCurrent?: boolean) {
		if (!canCreateDefault.value) {
			return;
		}

		const { scope } = scopeStore;
		if (scope.type === "space") {
			return createLayout({ type: "space", id: scope.id }, fromCurrent);
		} else if (scope.type === "distributor") {
			return createLayout(null, fromCurrent);
		}
	}

	const { isPublic } = useIsPublic();

	async function updateCurrentLayout(data: Partial<Pick<Dashboard, "panels" | "config">>) {
		if (
			selectedLayout.value == null ||
			currentLayout.value == null ||
			isPublic ||
			context.value == null
		) {
			return;
		}

		const updatedDashboard = await api.update(
			context.value.site,
			currentLayout.value.id,
			data.config ?? currentLayout.value.config,
			data.panels ?? currentLayout.value.panels
		);

		const type = getLayoutTypeFromScope(updatedDashboard.scope);
		availableLayouts.set(type, updatedDashboard);
	}

	async function deleteCurrentLayout() {
		if (
			currentLayout.value == null ||
			selectedLayout.value == null ||
			context.value == null ||
			isPublic
		) {
			return;
		}

		await api.delete(context.value.site, currentLayout.value.id);

		availableLayouts.delete(selectedLayout.value);
	}

	return {
		selectedLayout,
		currentLayout,
		availableLayouts,
		isLoading,
		possibleLayoutsTypes,
		editableLayoutsTypes,
		getLayoutTypeFromScope,

		isDistConfiguringSpace,
		isConfiguringPublicPage,

		creationLabel,
		label,

		hasLayoutType,

		canCreateDefault,
		canCreateOwn,
		canCreatePublic,
		isLayoutEditable,

		changeLayout,
		createLayout,
		createUserLayout,
		createDefaultLayout,
		createPublicLayout,

		updateCurrentLayout,
		deleteCurrentLayout,
	};
});
